import type { Dispatch } from 'redux'
import { ActionCreatorFactory } from 'typescript-fsa'
import { GPS, Wallet } from '@ynap/api-utils'
import { raygun } from '@ynap/clientside-monitoring'
import { timeout, delay } from '@ynap/promise-utils'
import { push } from 'connected-react-router'
import { asyncFactory, AsyncActionError } from '../../utils/redux-thunk-action'
import { mapCardToGPS } from './utils'

type State = {
    locale: { country: string; id: string }
    config: {
        head: { clientRequest: { timeout: number; delay: number } }
        global: {
            application: { brandId: string }
            services: {
                commerce: {
                    baseUrl: string
                    clientId: string
                }
                payment: { baseUrl: string }
            }
            watsonService
            paymentService
        }
        watsonService
        paymentService
    }
    router: { location: { pathname: string } }
    uberToken?: string
}

type CardValues = {
    cardHolderName: string
    number: string
    expiryDateStr: string
    cvv: string
}

/**
 * An authenticated API action creator to add a card to the wallet.
 * @param createAction - an instantiated `ActionCreatorFactory` from `typescript-fsa`
 */
export const addCardThunk = (createAction: ActionCreatorFactory) => {
    const createAuthApiAction = (name: string) => {
        const createAsyncAction = asyncFactory(createAction)
        return createAsyncAction(name, async (cardValues: CardValues, dispatch: Dispatch, getState, fetch: WindowOrWorkerGlobalScope['fetch']) => {
           const state = <State>getState()
            const { locale, config, router, uberToken } = state
            const { location } = router
            const { country } = locale
            const { pathname } = location || {}
            const { clientRequest } = config.head || config
            const { baseUrl, clientId, clientSecret } = config.watsonService || config.global.services.commerce
            const { brandId } = config.watsonService || config.global.application
            const paymentConfig = config.paymentService || config.global.services.payment
            const { baseUrl: paymentConfigBaseUrl } = paymentConfig
    
            if (!uberToken) {
                return dispatch(push(`/${locale.id}/account/login?redirect=${pathname}`))
            }
        
            return delay(timeout(fetch(addCardToGPS(cardValues, brandId, paymentConfigBaseUrl)), clientRequest.timeout), clientRequest.delay)
                .then((res) => {
                    if (res.ok) {
                        return res.json().then((json) => {
                            const cardToken = json.cardId
                            const addCardToWalletRequest = Wallet.addCard({
                                baseUrl,
                                brandId,
                                country,
                                uberToken,
                                clientId,
                                clientSecret,
                                cardToken,
                            })
    
                            return delay(timeout(fetch(addCardToWalletRequest), clientRequest.timeout), clientRequest.delay)
                                .then((res) => {
                                    const { ok, status } = res
                                    if (ok) {
                                        return res.json()
                                    } else if (status === 401) {
                                        return dispatch(push(`/${locale.id}/account/login?redirect=${pathname}`))
                                    }

                                    throw new AsyncActionError(res)
                                })
                                .catch((error) => {
                                    reportToRaygun(`Unsucessful response when hitting https://nonprod.ynap.biz/api/nap-int/wcs/resources/store/NAP_gb/wallet/card/gps/@self`, { error })
                                    throw new AsyncActionError(error)
                                })
                        })
                    }

                    throw new AsyncActionError(res)
                })
                .catch((error) => {
                    reportToRaygun(`Unsucessful response when hitting https://cards.int.gps.ynap.com/card-details`, { error })
                    throw new AsyncActionError(error)
                })
        })
    }
    return createAuthApiAction
}

const reportToRaygun = (message: string, { requestBody = null, error }) =>
    raygun.helpers.agent('send', {
        error: new Error(message),
        tags: ['gps', 'wallet', 'add-card'],
        customData: {
            requestBody,
            errorString: error.toString(),
        },
    })

// Adds mapped credit card and payment config to GPS
const addCardToGPS = (cardValues: CardValues, brandId: string, baseUrl: string) => {
    return GPS.addCreditCard({
        ...mapCardToGPS(cardValues),
        baseUrl,
        requestBrand: brandId,
    })
}
